import { useEffect, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { X, Copy, Share2, Zap } from "lucide-react";

export const ShareAppDialog = ({ onClose }) => {
  const [qrDataUrl, setQrDataUrl] = useState("");
  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : "";

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    QRCode.toDataURL(url, {
      width: 320,
      margin: 1,
      errorCorrectionLevel: "M",
      color: { dark: "#050505", light: "#FFFFFF" },
    })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(""));
    return () => {
      document.body.style.overflow = prev;
    };
  }, [url]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("LINK COPIED");
    } catch {
      toast.error("COPY FAILED");
    }
  };

  const handleShare = async () => {
    const payload = {
      title: "TimeFlash",
      text: "Schedule full-screen flash alerts for anything you can't miss.",
      url,
    };
    if (navigator.share && navigator.canShare?.(payload)) {
      try {
        await navigator.share(payload);
      } catch (err) {
        if (err?.name !== "AbortError") handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  return (
    <div
      data-testid="share-app-dialog"
      className="fixed inset-0 z-[9100] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white brutal-border brutal-shadow w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between brutal-border border-t-0 border-l-0 border-r-0 p-4 bg-[#00CCFF]">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5" strokeWidth={3} />
            <h3 className="font-display uppercase text-lg">Share TimeFlash</h3>
          </div>
          <button
            data-testid="share-app-close"
            onClick={onClose}
            className="bg-white brutal-border w-9 h-9 flex items-center justify-center brutal-shadow-sm brutal-interactive"
            aria-label="Close"
          >
            <X className="w-5 h-5" strokeWidth={3} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <p className="font-mono text-xs uppercase text-neutral-700 leading-relaxed">
            Scan the QR with any phone camera or send the link below. The app
            installs with one tap — no account needed.
          </p>

          <div className="flex items-center justify-center bg-white brutal-border p-4">
            {qrDataUrl ? (
              <img
                data-testid="share-qr"
                src={qrDataUrl}
                alt="QR code to install TimeFlash"
                className="w-64 h-64"
              />
            ) : (
              <div className="w-64 h-64 flex items-center justify-center font-mono text-xs uppercase text-neutral-500">
                Generating QR…
              </div>
            )}
          </div>

          <div className="brutal-border p-3 bg-[#F2F2F0] font-mono text-xs break-all">
            {url}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="share-copy"
              onClick={handleCopy}
              className="bg-white brutal-border font-display uppercase text-sm py-3 brutal-shadow-sm brutal-interactive flex items-center justify-center gap-2"
            >
              <Copy className="w-4 h-4" strokeWidth={3} /> Copy link
            </button>
            <button
              data-testid="share-share"
              onClick={handleShare}
              className="bg-[#FF3311] text-white brutal-border font-display uppercase text-sm py-3 brutal-shadow brutal-interactive flex items-center justify-center gap-2"
            >
              <Share2 className="w-4 h-4" strokeWidth={3} /> Share
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareAppDialog;
